// =============================================================================
// Origin UI — src/pages/reports/DocumentExpiryReportPage.tsx
// Document expiry report with summary cards, filters, and CSV export.
// WS-5 Batch 5.2 — Onboarding Lifecycle module.
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/apiService';
import styles from './DocumentExpiryReportPage.module.css';

// ─── Types ───

interface ExpirySummary {
  expired: number;
  expiringWithin30Days: number;
  expiringWithin90Days: number;
  active: number;
  totalTracked: number;
}

interface ExpiryItem {
  employeeVisaId: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  oqtId: string;
  officeName: string;
  documentType: string;
  visaNumber: string | null;
  expiryDate: string;
  daysRemaining: number;
  expiryStatus: string;
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'expired', label: 'Expired' },
  { value: 'critical', label: 'Critical (≤30 days)' },
  { value: 'expiring', label: 'Expiring (≤90 days)' },
];

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function getStatusBadge(status: string): { bg: string; color: string } {
  switch (status) {
    case 'Expired': return { bg: '#FEE2E2', color: '#991B1B' };
    case 'Critical': return { bg: '#FEF3C7', color: '#92400E' };
    case 'Expiring': return { bg: '#FEF9C3', color: '#854D0E' };
    case 'Active': return { bg: '#D1FAE5', color: '#065F46' };
    default: return { bg: '#F3F4F6', color: '#374151' };
  }
}

// ─── Component ───

export function DocumentExpiryReportPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ExpirySummary | null>(null);
  const [items, setItems] = useState<ExpiryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [officeFilter, setOfficeFilter] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [offices, setOffices] = useState<string[]>([]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadReport(); }, [statusFilter, officeFilter]);

  // Derive unique document types from loaded data
  const docTypes = useMemo(() => {
    const types = [...new Set(items.map(i => i.documentType))].filter(Boolean);
    types.sort();
    return types;
  }, [items]);

  // Client-side filtering for search + doc type (applied on top of server-filtered items)
  const filteredItems = useMemo(() => {
    let result = items;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(i =>
        i.employeeName.toLowerCase().includes(term) ||
        i.employeeNumber.toLowerCase().includes(term) ||
        (i.oqtId && i.oqtId.toLowerCase().includes(term))
      );
    }

    if (docTypeFilter) {
      result = result.filter(i => i.documentType === docTypeFilter);
    }

    return result;
  }, [items, searchTerm, docTypeFilter]);

  async function loadData() {
    try {
      setLoading(true);
      const [sum, report, officeList] = await Promise.all([
        api.get<ExpirySummary>('/api/expiry/summary'),
        api.get<ExpiryItem[]>('/api/expiry/report'),
        api.get<{ name: string }[]>('/api/reference/office-locations'),
      ]);
      setSummary(sum);
      setItems(report);
      setOffices(officeList.map(o => o.name));
    } catch (err: any) {
      setError(err.message || 'Failed to load expiry data');
    } finally {
      setLoading(false);
    }
  }

  async function loadReport() {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (officeFilter) params.set('office', officeFilter);
      const qs = params.toString();
      const report = await api.get<ExpiryItem[]>(`/api/expiry/report${qs ? `?${qs}` : ''}`);
      setItems(report);
    } catch {
      // Non-critical on filter change
    }
  }

  function exportCSV() {
    const headers = ['OQT ID', 'Employee', 'Employee #', 'Office', 'Document Type', 'Document #', 'Expiry Date', 'Days Remaining', 'Status'];
    const rows = filteredItems.map(i => [
      i.oqtId || '',
      i.employeeName,
      i.employeeNumber,
      i.officeName,
      i.documentType,
      i.visaNumber || '',
      i.expiryDate ? new Date(i.expiryDate).toISOString().split('T')[0] : '',
      i.daysRemaining.toString(),
      i.expiryStatus,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document-expiry-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function clearFilters() {
    setSearchTerm('');
    setStatusFilter('all');
    setOfficeFilter('');
    setDocTypeFilter('');
  }

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || officeFilter || docTypeFilter;

  if (loading) {
    return <div className={styles.page}><div className={styles.loading}>Loading expiry report...</div></div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Document Expiry Report</h1>
          <p className={styles.subtitle}>Visa and work permit expiry tracking</p>
        </div>
        <button className={styles.exportBtn} onClick={exportCSV} disabled={filteredItems.length === 0}>
          📥 Export CSV
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Summary cards */}
      {summary && (
        <div className={styles.summaryGrid}>
          <div className={`${styles.summaryCard} ${styles.summaryRed}`}>
            <div className={styles.summaryValue}>{summary.expired}</div>
            <div className={styles.summaryLabel}>Expired</div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryOrange}`}>
            <div className={styles.summaryValue}>{summary.expiringWithin30Days}</div>
            <div className={styles.summaryLabel}>Within 30 days</div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryAmber}`}>
            <div className={styles.summaryValue}>{summary.expiringWithin90Days}</div>
            <div className={styles.summaryLabel}>Within 90 days</div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryGreen}`}>
            <div className={styles.summaryValue}>{summary.active}</div>
            <div className={styles.summaryLabel}>Active (&gt;90 days)</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search Employee</label>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Name, OTI #, or OQT ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Status</label>
          <select className={styles.filterSelect} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Office</label>
          <select className={styles.filterSelect} value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
            <option value="">All Offices</option>
            {offices.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Document Type</label>
          <select className={styles.filterSelect} value={docTypeFilter} onChange={e => setDocTypeFilter(e.target.value)}>
            <option value="">All Types</option>
            {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className={styles.filterActions}>
          {hasActiveFilters && (
            <button className={styles.clearBtn} onClick={clearFilters}>Clear</button>
          )}
          <span className={styles.filterCount}>
            {filteredItems.length} record{filteredItems.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      {filteredItems.length === 0 ? (
        <div className={styles.empty}>No expiring documents found with current filters.</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Office</th>
                <th>Document Type</th>
                <th>Document #</th>
                <th>Expiry Date</th>
                <th>Days</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => {
                const badge = getStatusBadge(item.expiryStatus);
                return (
                  <tr
                    key={item.employeeVisaId}
                    className={styles.tableRow}
                    onClick={() => navigate(`/employees/${item.employeeId}`)}
                  >
                    <td>
                      <div className={styles.empName}>{item.employeeName}</div>
                      <div className={styles.empNum}>{item.oqtId || item.employeeNumber}</div>
                    </td>
                    <td>{item.officeName}</td>
                    <td>{item.documentType}</td>
                    <td>{item.visaNumber || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.expiryDate)}</td>
                    <td>
                      <span className={item.daysRemaining < 0 ? styles.daysNeg : item.daysRemaining <= 30 ? styles.daysWarn : styles.daysOk}>
                        {item.daysRemaining < 0 ? `${Math.abs(item.daysRemaining)}d overdue` : `${item.daysRemaining}d`}
                      </span>
                    </td>
                    <td>
                      <span className={styles.statusBadge} style={{ background: badge.bg, color: badge.color }}>
                        {item.expiryStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
